import { useCallback, useState } from 'react'
import { api, describeError, type AdminUser } from '../lib/api'
import { formatPresence } from '../lib/format'
import { useLoad } from '../hooks/useLoad'
import { Avatar } from '../components/Avatar'
import { IconButton } from '../components/IconButton'
import { RefreshIcon } from '../components/Icons'
import { SectionHeading } from '../components/SectionHeading'
import { Spinner } from '../components/Spinner'

/**
 * The pending queue: `GET /api/admin/users?status=pending`, and `PATCH /api/admin/users/:id` to
 * decide (docs/CONTRACT.md §4, §11). This is the console's reason to exist, so it is the page a
 * sign-in lands on. Approving is the only action that opens the API for that account.
 */
export function QueuePage() {
  const load = useCallback(() => api.adminUsers('pending'), [])
  const { data, loading, error, reload } = useLoad(load, (caught) =>
    describeError(caught, 'Could not load the pending queue.'),
  )
  const [busyId, setBusyId] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const decide = async (user: AdminUser, status: 'approved' | 'rejected') => {
    setBusyId(user.id)
    setActionError(null)
    try {
      await api.adminUpdateUser(user.id, { status })
      reload()
    } catch (caught) {
      setActionError(describeError(caught, 'Could not update that account.'))
    } finally {
      setBusyId(null)
    }
  }

  return (
    <section aria-labelledby="queue-heading">
      <SectionHeading
        id="queue-heading"
        action={
          <IconButton label="Refresh the queue" size={36} onClick={reload} disabled={loading}>
            <RefreshIcon size={16} />
          </IconButton>
        }
      >
        Waiting for approval
        {data && <span className="numeric ms-2 text-muted">{data.users.length}</span>}
      </SectionHeading>

      <p className="mb-3 text-meta text-muted">
        New sign-ups land here. An account can do nothing until it is approved.
      </p>

      {loading && !data && (
        <div className="flex justify-center py-10 text-muted">
          <Spinner label="Loading the queue" />
        </div>
      )}

      {error && (
        <p role="alert" className="text-ui text-danger">
          {error}
        </p>
      )}

      {actionError && (
        <p role="alert" className="mb-2 text-ui text-danger">
          {actionError}
        </p>
      )}

      {data && data.users.length === 0 && (
        <div className="rounded-panel bg-surface p-5 ring-1 ring-line ring-inset">
          <p className="text-ui text-ink">The queue is clear.</p>
          <p className="mt-1 text-ui text-muted">
            No sign-ups are waiting. A new account appears here the moment it is created.
          </p>
        </div>
      )}

      {data && data.users.length > 0 && (
        <ul className="flex flex-col gap-2">
          {data.users.map((user) => (
            <li
              key={user.id}
              className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-panel bg-surface p-3 ring-1 ring-line ring-inset"
            >
              <Avatar name={user.displayName} path={user.avatarUrl} size={40} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-ui font-medium text-ink">
                  <bdi>{user.displayName}</bdi>
                </span>
                <span className="block truncate text-meta text-muted">
                  @{user.handle} · {formatPresence(user.online, user.lastSeenAt)}
                </span>
              </span>
              <span className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={busyId !== null}
                  onClick={() => void decide(user, 'approved')}
                  className="press flex min-h-11 items-center gap-2 rounded-full bg-accent px-4 text-ui font-semibold text-on-accent hover:bg-accent-strong disabled:opacity-50"
                >
                  {busyId === user.id && <Spinner size={16} label="Approving" />}
                  Approve
                </button>
                <button
                  type="button"
                  disabled={busyId !== null}
                  onClick={() => void decide(user, 'rejected')}
                  className="press min-h-11 rounded-full px-4 text-ui font-medium text-danger ring-1 ring-danger/40 ring-inset hover:bg-danger/12 disabled:opacity-50"
                >
                  Reject
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
